import { PAYMENT_TYPE } from './constants.js';
const CARD_LIKE_PAYMENT_KEYS = new Set([
    PAYMENT_TYPE.CARD,
    PAYMENT_TYPE.INVOICE,
    PAYMENT_TYPE.POS,
    'online',
    'credit_card',
    'in_person_card',
]);
function toMoney(value) {
    if (value === null || value === undefined || value === '')
        return 0;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}
function roundMoney(n) {
    return Math.round(n * 100) / 100;
}
function normalizePaymentKey(value) {
    return String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/-/g, '_');
}
export function hasDestinationAccountNet(row) {
    const dest = row.destinationAccountNet;
    return dest !== null && dest !== undefined && dest !== '';
}
/**
 * Card/invoice/POS flows charge custom fees through Stripe, so processing fee
 * already includes the pass-through on additional fees.
 */
export function isCardLikePaymentListing(row) {
    const keys = [
        normalizePaymentKey(row.paymentOption),
        normalizePaymentKey(row.paymentType),
        normalizePaymentKey(row.paymentMethod),
    ].filter(Boolean);
    return keys.some((k) => CARD_LIKE_PAYMENT_KEYS.has(k));
}
/** Voucher redemption orders do not charge Stripe processing fees. */
export function isVoucherPaymentListing(row) {
    if (row.voucher === true || row.voucher === 't')
        return true;
    const keys = [
        normalizePaymentKey(row.paymentOption),
        normalizePaymentKey(row.paymentType),
        normalizePaymentKey(row.paymentMethod),
    ].filter(Boolean);
    return keys.some((k) => k === PAYMENT_TYPE.VOUCHER);
}
export function getPaymentListingNet(row) {
    if (hasDestinationAccountNet(row)) {
        const n = Number(row.destinationAccountNet);
        if (Number.isFinite(n))
            return n;
    }
    return toMoney(row.net);
}
export function getPaymentListingProcessingFee(row) {
    if (isVoucherPaymentListing(row))
        return 0;
    return toMoney(row.estimatedProcessingFee ?? row.processingFee);
}
function rawAdditionalFees(row) {
    return toMoney(row.totalFeeAmount);
}
/**
 * Additional fees as they were counted before custom fees were excluded from
 * net for every payment type. Only used to calibrate payment-plan proration
 * against stored net, which still carries the old semantics.
 */
function legacyNonCardAdditionalFees(row) {
    const total = toMoney(row.totalFeeAmount);
    if (!total || isCardLikePaymentListing(row))
        return 0;
    return total;
}
function computeFullExpectedNet(row) {
    const gross = toMoney(row.gross);
    const processingFee = getPaymentListingProcessingFee(row);
    const chargeback = toMoney(row.chargebackProtection);
    const additional = legacyNonCardAdditionalFees(row);
    const serviceFee = toMoney(row.serviceFee);
    return roundMoney(gross - serviceFee - processingFee - chargeback - additional);
}
/**
 * Fraction of full-order fees/gross that matches collected net on payment-plan rows.
 * Destination-charge installment rows already store installment-level amounts.
 */
export function getPaymentPlanProrationFactor(row) {
    if (!row.paymentPlan || hasDestinationAccountNet(row))
        return 1;
    const net = getPaymentListingNet(row);
    const fullExpectedNet = computeFullExpectedNet(row);
    if (fullExpectedNet > 0.0001) {
        return Math.max(0, Math.min(1, net / fullExpectedNet));
    }
    const installmentCount = Math.floor(Number(row.paymentPlanInstallmentCount));
    if (installmentCount > 0) {
        return 1 / installmentCount;
    }
    return 1;
}
function prorateForPaymentPlan(row, amount) {
    if (!row.paymentPlan)
        return amount;
    return roundMoney(amount * getPaymentPlanProrationFactor(row));
}
/**
 * Custom fees charged on top of the ticket price. Always excluded from net:
 * for card-like payments the processing fee only covers the pass-through *on*
 * the custom fee, so the fee itself is still sitting in gross.
 */
export function getPaymentListingAdditionalFees(row) {
    const total = rawAdditionalFees(row);
    if (!total)
        return 0;
    return prorateForPaymentPlan(row, total);
}
export function getPaymentListingFacilityFee(row) {
    return prorateForPaymentPlan(row, toMoney(row.facilityFee ?? row.fees?.facility));
}
export function getPaymentListingSalesTax(row) {
    return prorateForPaymentPlan(row, toMoney(row.salesTax));
}
export function getPaymentListingDonationTotal(row) {
    return prorateForPaymentPlan(row, toMoney(row.donationTotal));
}
/** Raw service fee (optionally prorated for non-destination payment plans). */
export function getPaymentListingDisplayServiceFee(row) {
    return prorateForPaymentPlan(row, toMoney(row.serviceFee));
}
/**
 * Service fee used in report reconciliation totals.
 * Always deducted: on destination charges the platform retains service +
 * processing as the application fee, so it never reaches the org either.
 */
export function getPaymentListingReportServiceFee(row) {
    return getPaymentListingDisplayServiceFee(row);
}
export function getPaymentListingReportGross(row) {
    return prorateForPaymentPlan(row, toMoney(row.gross));
}
export function getPaymentListingReportChargebackProtection(row) {
    return prorateForPaymentPlan(row, toMoney(row.chargebackProtection));
}
/**
 * Processing fee shown on Order Listings / Season Report.
 * Prefers estimated processing fee when present, else order processing fee
 * (optionally prorated for non-destination payment plans).
 */
export function getPaymentListingReportProcessingFee(row) {
    return prorateForPaymentPlan(row, getPaymentListingProcessingFee(row));
}
function isResalePaymentListing(row) {
    const type = String(row.type ?? '')
        .trim()
        .toLowerCase();
    if (type)
        return type === 'resale';
    return (String(row.productType ?? '')
        .trim()
        .toLowerCase() === 'resale');
}
/**
 * Net shown on Order Listings / Season Report. Always derived from raw order
 * columns so every report agrees; stored net is never read.
 */
export function getPaymentListingReportNet(row) {
    return getPaymentListingDerivedNet(row);
}
/**
 * Canonical report net. A resale order contributes the organizer royalty.
 * Every other order is:
 * Gross − Service Fee − Processing Fee − Chargeback − Facility Fee
 *       − Custom Fees − Donation Total − Sales Tax
 * (service fee is 0 for destination-charge rows)
 */
export function getPaymentListingDerivedNet(row) {
    if (isResalePaymentListing(row)) {
        return roundMoney(toMoney(row.resaleOrganizerShare));
    }
    return roundMoney(getPaymentListingReportGross(row) -
        getPaymentListingReportServiceFee(row) -
        getPaymentListingReportProcessingFee(row) -
        getPaymentListingReportChargebackProtection(row) -
        getPaymentListingFacilityFee(row) -
        getPaymentListingAdditionalFees(row) -
        getPaymentListingDonationTotal(row) -
        getPaymentListingSalesTax(row));
}
export function isPaymentListingRowReconcilable(row) {
    if (!row.paymentPlan)
        return true;
    if (hasDestinationAccountNet(row))
        return true;
    const fullExpectedNet = computeFullExpectedNet(row);
    if (fullExpectedNet > 0.0001)
        return true;
    return (Number(row.paymentPlanInstallmentCount) || 0) > 0;
}
